import { useState, useEffect, useRef } from 'react';
import { Brain, Upload, Search, AlertTriangle, Crosshair, Info, Database, Cpu, Plus, X, FileUp, Trash2 } from 'lucide-react';
import { axios } from '../App';
import { toast } from 'sonner';

const EMPTY_DRONE_FORM = {
  name: '', manufacturer: '', category: '', origin: '',
  maxRange: '', maxAltitude: '', maxSpeed: '', flightTime: '',
  weight: '', payload: '', rfFrequency: '',
  signatures: '', threatLevel: 'medium', description: ''
};

export default function DroneClassification() {
  const [inputData, setInputData] = useState({
    rfSignature: '', radarProfile: '', visualSignature: '',
    acousticSignature: '', flightPattern: '', speed: '',
    altitude: '', additionalNotes: ''
  });
  const [classificationResult, setClassificationResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDrone, setSelectedDrone] = useState(null);
  const [droneDatabase, setDroneDatabase] = useState([]);
  const [loadingDb, setLoadingDb] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [droneForm, setDroneForm] = useState(EMPTY_DRONE_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchDroneDatabase(); }, []);

  const fetchDroneDatabase = async () => {
    setLoadingDb(true);
    try {
      const res = await axios.get('/drone-database');
      setDroneDatabase(res.data);
    } catch (err) {
      toast.error('Failed to load drone database');
    } finally {
      setLoadingDb(false);
    }
  };

  const handleInputChange = (field, value) => {
    setInputData(prev => ({ ...prev, [field]: value }));
  };

  const analyzeWithAI = async () => {
    setIsAnalyzing(true);
    setClassificationResult(null);
    try {
      const response = await axios.post('/ai/classify-drone', { ...inputData });
      if (response.data) {
        const aiResult = response.data;
        const matchedDrone = aiResult.matchedDroneId
          ? droneDatabase.find(d => d.id === aiResult.matchedDroneId) || null
          : null;
        setClassificationResult({
          matchedDrone, confidence: aiResult.confidence || 0,
          analysis: aiResult.analysis || '', recommendations: aiResult.recommendations || [],
          droneType: aiResult.droneType, threatLevel: aiResult.threatLevel
        });
      }
    } catch {
      // Fallback: local matching
      const matches = droneDatabase.filter(drone => {
        const inputText = Object.values(inputData).join(' ').toLowerCase();
        return (drone.signatures || []).some(sig => inputText.includes(sig)) ||
               inputText.includes(drone.name.toLowerCase()) ||
               inputText.includes((drone.manufacturer || '').toLowerCase());
      });
      if (matches.length > 0) {
        const best = matches[0];
        setClassificationResult({
          matchedDrone: best, confidence: 75 + Math.random() * 20,
          analysis: `Based on provided signatures, this appears to be a ${best.name} or similar ${best.category} drone. ${best.description}`,
          recommendations: [
            `Monitor RF: ${best.rfFrequency}`, `Threat level: ${(best.threatLevel || '').toUpperCase()}`,
            best.threatLevel === 'high' ? 'Recommend immediate countermeasure deployment' : 'Continue tracking'
          ]
        });
      } else {
        setClassificationResult({
          matchedDrone: null, confidence: 0,
          analysis: 'Unable to classify with provided data. Provide more specific signatures.',
          recommendations: ['Capture additional RF data', 'Obtain visual confirmation', 'Check acoustic patterns']
        });
      }
    } finally { setIsAnalyzing(false); }
  };

  const quickClassify = (droneType) => {
    const presets = {
      'quadcopter': { visualSignature: 'quadcopter, 4 rotors', acousticSignature: 'high-pitch multi-rotor', rfSignature: '2.4 GHz spread spectrum' },
      'fixed-wing': { visualSignature: 'fixed wing, single prop', acousticSignature: 'low frequency propeller', rfSignature: 'satellite uplink detected' },
      'fpv': { visualSignature: 'small quadcopter, racing style', acousticSignature: 'high-speed rotor noise', rfSignature: '5.8 GHz video, 900 MHz control' },
      'loitering': { visualSignature: 'delta wing, pusher prop', acousticSignature: 'constant engine drone', rfSignature: 'GPS signals only, no control link' }
    };
    if (presets[droneType]) {
      setInputData(prev => ({ ...prev, ...presets[droneType] }));
      toast.success(`Loaded ${droneType} signature preset`);
    }
  };

  const handleAddDrone = async () => {
    if (!droneForm.name.trim()) { toast.error('Drone name is required'); return; }
    setIsSubmitting(true);
    try {
      const payload = {
        ...droneForm,
        signatures: droneForm.signatures ? droneForm.signatures.split(',').map(s => s.trim()).filter(Boolean) : []
      };
      await axios.post('/drone-database', payload);
      toast.success(`${droneForm.name} added to database`);
      setDroneForm(EMPTY_DRONE_FORM);
      setShowAddForm(false);
      fetchDroneDatabase();
    } catch (err) {
      toast.error('Failed to add drone');
    } finally { setIsSubmitting(false); }
  };

  const handleDeleteDrone = async (e, droneId, droneName) => {
    e.stopPropagation();
    try {
      await axios.delete(`/drone-database/${droneId}`);
      toast.success(`${droneName} removed`);
      setSelectedDrone(null);
      fetchDroneDatabase();
    } catch { toast.error('Failed to delete'); }
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const values = [];
      let current = '', inQuotes = false;
      for (const ch of line) {
        if (ch === '"') { inQuotes = !inQuotes; }
        else if (ch === ',' && !inQuotes) { values.push(current.trim()); current = ''; }
        else { current += ch; }
      }
      values.push(current.trim());
      const obj = {};
      headers.forEach((h, i) => {
        const key = h.replace(/\s+/g, '');
        const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
        obj[camelKey] = values[i] || '';
      });
      // Parse signatures from comma-separated if wrapped in quotes
      if (typeof obj.signatures === 'string') {
        obj.signatures = obj.signatures.split(';').map(s => s.trim()).filter(Boolean);
      }
      return obj;
    });
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsImporting(true);
    try {
      const text = await file.text();
      let drones = [];
      if (file.name.endsWith('.json')) {
        const parsed = JSON.parse(text);
        drones = Array.isArray(parsed) ? parsed : [parsed];
      } else if (file.name.endsWith('.csv')) {
        drones = parseCSV(text);
      } else {
        toast.error('Unsupported format. Use JSON or CSV.');
        return;
      }
      if (drones.length === 0) { toast.error('No valid drone entries found'); return; }
      // Normalize entries
      const normalized = drones.map(d => ({
        name: d.name || 'Unknown Drone',
        manufacturer: d.manufacturer || 'Unknown',
        category: d.category || 'Unknown',
        origin: d.origin || 'Unknown',
        maxRange: d.maxRange || d.max_range || 'N/A',
        maxAltitude: d.maxAltitude || d.max_altitude || 'N/A',
        maxSpeed: d.maxSpeed || d.max_speed || 'N/A',
        flightTime: d.flightTime || d.flight_time || 'N/A',
        weight: d.weight || 'N/A',
        payload: d.payload || 'N/A',
        rfFrequency: d.rfFrequency || d.rf_frequency || 'N/A',
        signatures: Array.isArray(d.signatures) ? d.signatures : (d.signatures || '').split(',').map(s => s.trim()).filter(Boolean),
        threatLevel: d.threatLevel || d.threat_level || 'medium',
        description: d.description || ''
      }));
      const res = await axios.post('/drone-database/import-data', normalized);
      toast.success(`Imported ${res.data.imported_count} drones from ${file.name}`);
      fetchDroneDatabase();
    } catch (err) {
      toast.error(`Import failed: ${err.message || 'Invalid file format'}`);
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filteredDatabase = droneDatabase.filter(drone =>
    (drone.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (drone.manufacturer || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (drone.category || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getThreatColor = (level) => {
    switch (level) {
      case 'high': return 'text-red-500 bg-red-500/20 border-red-500';
      case 'medium': return 'text-orange-500 bg-orange-500/20 border-orange-500';
      case 'low': return 'text-green-500 bg-green-500/20 border-green-500';
      default: return 'text-zinc-500 bg-zinc-500/20 border-zinc-500';
    }
  };

  const getSourceBadge = (source) => {
    switch (source) {
      case 'client-library': return <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded">library</span>;
      case 'manual': return <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded">manual</span>;
      default: return <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase bg-zinc-500/20 text-zinc-400 border border-zinc-500/30 rounded">default</span>;
    }
  };

  return (
    <div className="h-full flex flex-col bg-zinc-950 overflow-hidden" data-testid="drone-classification-page">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
              <Brain size={24} className="text-purple-500" />
              AI Drone Classification Engine
            </h1>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
              Open Source Intelligence Database | Machine Learning Classification
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs font-mono text-zinc-400">
            <Database size={14} />
            <span data-testid="drone-count">{droneDatabase.length} Drones in Database</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Input & Classification */}
        <div className="w-1/2 border-r border-zinc-800 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-zinc-800">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
              <Cpu size={16} className="text-sky-500" />
              Sensor Input Data
            </h3>
            <div className="flex gap-2 mb-4">
              <span className="text-xs text-zinc-500">Quick Presets:</span>
              {['quadcopter', 'fixed-wing', 'fpv', 'loitering'].map(type => (
                <button key={type} onClick={() => quickClassify(type)}
                  className="px-2 py-1 text-xs font-mono uppercase bg-zinc-800 hover:bg-zinc-700 rounded border border-zinc-700 text-zinc-300"
                  data-testid={`preset-${type}`}>
                  {type}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['rfSignature', 'RF Signature', '2.4 GHz spread spectrum'],
                ['radarProfile', 'Radar Profile', 'small RCS, low altitude'],
                ['visualSignature', 'Visual Signature', 'quadcopter, white, 4 rotors'],
                ['acousticSignature', 'Acoustic Signature', 'high-pitch multi-rotor'],
                ['speed', 'Speed (km/h)', '60'],
                ['altitude', 'Altitude (m)', '150']
              ].map(([field, label, ph]) => (
                <div key={field}>
                  <label className="block text-xs text-zinc-400 mb-1">{label}</label>
                  <input type="text" value={inputData[field]}
                    onChange={(e) => handleInputChange(field, e.target.value)}
                    placeholder={`e.g., ${ph}`}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500"
                    data-testid={`input-${field}`} />
                </div>
              ))}
            </div>
            <div className="mt-3">
              <label className="block text-xs text-zinc-400 mb-1">Additional Notes / Flight Pattern</label>
              <textarea value={inputData.additionalNotes}
                onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                placeholder="e.g., circular pattern, hovering, approaching from NW" rows={2}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500 resize-none"
                data-testid="input-additionalNotes" />
            </div>
            <button onClick={analyzeWithAI} disabled={isAnalyzing}
              className="w-full mt-4 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed rounded font-mono text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
              data-testid="classify-btn">
              {isAnalyzing ? (<><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Analyzing...</>) :
                (<><Brain size={18} /> Classify Drone</>)}
            </button>
          </div>

          {/* Classification Result */}
          <div className="flex-1 p-4 overflow-y-auto">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
              <Crosshair size={16} className="text-red-500" /> Classification Result
            </h3>
            {!classificationResult && !isAnalyzing && (
              <div className="text-center py-8 text-zinc-500">
                <Brain size={48} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">Enter sensor data and click "Classify Drone" to analyze</p>
              </div>
            )}
            {classificationResult && (
              <div className="space-y-4">
                {classificationResult.matchedDrone ? (
                  <>
                    <div className="bg-zinc-900 border border-zinc-800 rounded p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="text-lg font-bold text-white">{classificationResult.matchedDrone.name}</h4>
                          <p className="text-sm text-zinc-400">{classificationResult.matchedDrone.manufacturer} | {classificationResult.matchedDrone.origin}</p>
                        </div>
                        <div className={`px-3 py-1 rounded border text-xs font-mono uppercase ${getThreatColor(classificationResult.matchedDrone.threatLevel)}`}>
                          {classificationResult.matchedDrone.threatLevel} Threat
                        </div>
                      </div>
                      <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-zinc-400">Match Confidence</span>
                          <span className="text-purple-400 font-mono">{Number(classificationResult.confidence).toFixed(1)}%</span>
                        </div>
                        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div className="h-full bg-purple-500 transition-all duration-500" style={{ width: `${classificationResult.confidence}%` }} />
                        </div>
                      </div>
                      <p className="text-sm text-zinc-300">{classificationResult.analysis || classificationResult.matchedDrone.description}</p>
                    </div>
                    <div className="bg-zinc-900 border border-zinc-800 rounded p-4">
                      <h5 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Specifications</h5>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {[['Category', 'category'], ['Max Range', 'maxRange'], ['Max Speed', 'maxSpeed'],
                          ['Max Altitude', 'maxAltitude'], ['Flight Time', 'flightTime'], ['Weight', 'weight']
                        ].map(([label, key]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-zinc-500">{label}:</span>
                            <span className="text-zinc-200 font-mono">{classificationResult.matchedDrone[key]}</span>
                          </div>
                        ))}
                        <div className="flex justify-between col-span-2">
                          <span className="text-zinc-500">RF Frequency:</span>
                          <span className="text-zinc-200 font-mono">{classificationResult.matchedDrone.rfFrequency}</span>
                        </div>
                        <div className="flex justify-between col-span-2">
                          <span className="text-zinc-500">Payload:</span>
                          <span className="text-zinc-200 font-mono">{classificationResult.matchedDrone.payload}</span>
                        </div>
                      </div>
                    </div>
                    {classificationResult.recommendations?.length > 0 && (
                      <div className="bg-zinc-900 border border-zinc-800 rounded p-4">
                        <h5 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-2">
                          <AlertTriangle size={14} className="text-yellow-500" /> Recommendations
                        </h5>
                        <ul className="space-y-2">
                          {classificationResult.recommendations.map((rec, i) => (
                            <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                              <span className="text-purple-500">•</span>{rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-zinc-900 border border-zinc-800 rounded p-4">
                    <div className="flex items-center gap-2 text-yellow-500 mb-3">
                      <AlertTriangle size={20} /><span className="font-bold">No Match Found</span>
                    </div>
                    <p className="text-sm text-zinc-400 mb-4">{classificationResult.analysis}</p>
                    <ul className="space-y-2">
                      {(classificationResult.recommendations || []).map((rec, i) => (
                        <li key={i} className="text-sm text-zinc-300 flex items-start gap-2">
                          <span className="text-yellow-500">•</span>{rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Database */}
        <div className="w-1/2 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-zinc-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                <Database size={16} className="text-green-500" />
                Drone Database
              </h3>
              <div className="flex items-center gap-2">
                <input type="file" ref={fileInputRef} accept=".json,.csv" onChange={handleFileImport} className="hidden" data-testid="file-import-input" />
                <button onClick={() => fileInputRef.current?.click()} disabled={isImporting}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 rounded transition-colors disabled:opacity-50"
                  data-testid="import-library-btn">
                  {isImporting ? <div className="w-3 h-3 border border-blue-400/30 border-t-blue-400 rounded-full animate-spin" /> : <FileUp size={14} />}
                  {isImporting ? 'Importing...' : 'Load Library'}
                </button>
                <button onClick={() => setShowAddForm(!showAddForm)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase bg-green-600/20 hover:bg-green-600/30 text-green-400 border border-green-500/30 rounded transition-colors"
                  data-testid="add-drone-btn">
                  {showAddForm ? <X size={14} /> : <Plus size={14} />}
                  {showAddForm ? 'Cancel' : 'Add Drone'}
                </button>
              </div>
            </div>

            {/* Add Drone Form */}
            {showAddForm && (
              <div className="mb-3 p-3 bg-zinc-900 border border-green-500/30 rounded space-y-3" data-testid="add-drone-form">
                <h4 className="text-xs font-bold uppercase tracking-wider text-green-400">New Drone Entry</h4>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ['name', 'Name *'], ['manufacturer', 'Manufacturer'], ['category', 'Category'], ['origin', 'Origin'],
                    ['maxRange', 'Max Range'], ['maxAltitude', 'Max Altitude'], ['maxSpeed', 'Max Speed'], ['flightTime', 'Flight Time'],
                    ['weight', 'Weight'], ['payload', 'Payload'], ['rfFrequency', 'RF Frequency']
                  ].map(([key, label]) => (
                    <div key={key}>
                      <label className="block text-[10px] text-zinc-500 mb-0.5">{label}</label>
                      <input type="text" value={droneForm[key]}
                        onChange={e => setDroneForm(prev => ({ ...prev, [key]: e.target.value }))}
                        className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-green-500"
                        placeholder={label} data-testid={`form-${key}`} />
                    </div>
                  ))}
                  <div>
                    <label className="block text-[10px] text-zinc-500 mb-0.5">Threat Level</label>
                    <select value={droneForm.threatLevel}
                      onChange={e => setDroneForm(prev => ({ ...prev, threatLevel: e.target.value }))}
                      className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-white focus:outline-none focus:border-green-500"
                      data-testid="form-threatLevel">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] text-zinc-500 mb-0.5">Signatures (comma-separated)</label>
                  <input type="text" value={droneForm.signatures}
                    onChange={e => setDroneForm(prev => ({ ...prev, signatures: e.target.value }))}
                    className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-green-500"
                    placeholder="e.g., quadcopter, thermal, gps" data-testid="form-signatures" />
                </div>
                <div>
                  <label className="block text-[10px] text-zinc-500 mb-0.5">Description</label>
                  <textarea value={droneForm.description}
                    onChange={e => setDroneForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-2 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-green-500 resize-none"
                    rows={2} placeholder="Brief description" data-testid="form-description" />
                </div>
                <button onClick={handleAddDrone} disabled={isSubmitting}
                  className="w-full py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-800 rounded text-xs font-mono uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
                  data-testid="submit-drone-btn">
                  {isSubmitting ? <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" /> : <Plus size={14} />}
                  {isSubmitting ? 'Adding...' : 'Add to Database'}
                </button>
              </div>
            )}

            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search drones..."
                className="w-full pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-green-500"
                data-testid="search-drones" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {loadingDb ? (
              <div className="text-center py-8 text-zinc-500 text-sm">Loading drone database...</div>
            ) : filteredDatabase.length === 0 ? (
              <div className="text-center py-8 text-zinc-500 text-sm">No drones found</div>
            ) : (
              <div className="space-y-2">
                {filteredDatabase.map(drone => (
                  <div key={drone.id}
                    onClick={() => setSelectedDrone(selectedDrone?.id === drone.id ? null : drone)}
                    className={`bg-zinc-900 border rounded p-3 cursor-pointer transition-all ${
                      selectedDrone?.id === drone.id ? 'border-green-500' : 'border-zinc-800 hover:border-zinc-700'
                    }`} data-testid={`drone-card-${drone.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div>
                          <h4 className="font-bold text-white text-sm">{drone.name}</h4>
                          <p className="text-xs text-zinc-500">{drone.manufacturer} | {drone.category}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getSourceBadge(drone.source)}
                        <div className={`px-2 py-0.5 rounded border text-xs font-mono uppercase ${getThreatColor(drone.threatLevel)}`}>
                          {drone.threatLevel}
                        </div>
                        {drone.source !== 'default' && (
                          <button onClick={(e) => handleDeleteDrone(e, drone.id, drone.name)}
                            className="p-1 text-zinc-500 hover:text-red-400 transition-colors"
                            data-testid={`delete-drone-${drone.id}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                    {selectedDrone?.id === drone.id && (
                      <div className="mt-3 pt-3 border-t border-zinc-800 space-y-2 text-xs">
                        <div className="grid grid-cols-2 gap-2">
                          <div><span className="text-zinc-500">Origin:</span> <span className="text-zinc-300">{drone.origin}</span></div>
                          <div><span className="text-zinc-500">Range:</span> <span className="text-zinc-300">{drone.maxRange}</span></div>
                          <div><span className="text-zinc-500">Speed:</span> <span className="text-zinc-300">{drone.maxSpeed}</span></div>
                          <div><span className="text-zinc-500">Altitude:</span> <span className="text-zinc-300">{drone.maxAltitude}</span></div>
                          <div><span className="text-zinc-500">Flight Time:</span> <span className="text-zinc-300">{drone.flightTime}</span></div>
                          <div><span className="text-zinc-500">Weight:</span> <span className="text-zinc-300">{drone.weight}</span></div>
                        </div>
                        <div><span className="text-zinc-500">Payload:</span> <span className="text-zinc-300">{drone.payload}</span></div>
                        <div><span className="text-zinc-500">RF:</span> <span className="text-zinc-300">{drone.rfFrequency}</span></div>
                        <p className="text-zinc-400 italic">{drone.description}</p>
                        {(drone.signatures || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {drone.signatures.map(sig => (
                              <span key={sig} className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-400 text-xs">{sig}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-zinc-800 bg-zinc-900/50">
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <Info size={14} />
              <span>Data sourced from open intelligence databases, manual entries, and client libraries</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
